import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import { apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../common/rtCommon.css';
import './AddBuilding.css';
import { OCCUPANCY_TYPE_OPTIONS } from '../../utils/occupancyConstants';
import RTPageLayout from '../common/RTPageLayout';

const initialBuilding = {
  name: '',
  address: '',
  type: 'rental',
  occupancyGender: '',
  startDate: new Date(), // Use Date object for react-datepicker
};

const initialRental = {
  ownerName: '',
  ownerPhone: '',
  ownerAddress: '',
  advance: '',
  rent: '',
};

const AddBuilding = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [building, setBuilding] = useState(initialBuilding);
  const [rental, setRental] = useState(initialRental);
  const [errors, setErrors] = useState({});

  const handleBuildingChange = (e) => {
    const { name, value } = e.target;
    setBuilding((prev) => ({ ...prev, [name]: value }));
    if (name === 'type' && value === 'own') {
      setRental(initialRental);
    }
  };

  const handleDateChange = (date) => {
    setBuilding((prev) => ({ ...prev, startDate: date }));
    setErrors((prev) => ({ ...prev, startDate: undefined }));
  };

  const handleRentalChange = (e) => {
    const { name, value } = e.target;
    setRental((prev) => ({ ...prev, [name]: value }));
  };

  const handleSameAddressChange = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      setRental((prev) => ({ ...prev, ownerAddress: building.address }));
    } else {
      setRental((prev) => ({ ...prev, ownerAddress: '' }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!building.name || building.name.trim() === '') {
      errs.name = 'Building Name is required';
    }
    if (!building.address || building.address.trim() === '') {
      errs.address = 'Building Address is required';
    }
    if (!building.startDate) {
      errs.startDate = 'Start Date is required';
    }
    if (!building.occupancyGender) {
      errs.occupancyGender = 'Occupancy gender is required';
    }
    if (building.type === 'rental') {
      if (!rental.ownerName || rental.ownerName.trim() === '') {
        errs.ownerName = 'Owner Name is required';
      }
      // Owner Phone is now optional, but validate format if provided
      if (
        rental.ownerPhone &&
        rental.ownerPhone.trim() !== '' &&
        !/^\d{10}$/.test(rental.ownerPhone.trim())
      ) {
        errs.ownerPhone = 'Enter a valid 10 digit phone number';
      }
      if (!rental.rent || rental.rent.toString().trim() === '') {
        errs.rent = 'Rent is required';
      } else if (isNaN(Number(rental.rent)) || Number(rental.rent) < 0) {
        errs.rent = 'Enter a valid rent amount (0 or greater)';
      }
      if (
        rental.advance &&
        (isNaN(Number(rental.advance)) || Number(rental.advance) < 0)
      ) {
        errs.advance = 'Enter a valid advance amount';
      }
    }
    return errs;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      const data = {
        name: building.name,
        address: building.address,
        type: building.type,
        occupancy_gender: building.occupancyGender,
        status: 'active',
        start_date: building.startDate
          ? building.startDate.toISOString().split('T')[0]
          : null,
        owner_name: building.type === 'rental' ? rental.ownerName : null,
        owner_phone: building.type === 'rental' ? rental.ownerPhone : null,
        owner_address: building.type === 'rental' ? rental.ownerAddress : null,
        advance:
          building.type === 'rental' && rental.advance === ''
            ? null
            : Number(rental.advance),
        rent:
          building.type === 'rental' && rental.rent === ''
            ? null
            : Number(rental.rent),
      };
      try {
        const response = await apiPost('/building', data);
        if (
          response &&
          response.status == 200 &&
          response.data &&
          response.data.building_id
        ) {
          toastSuccess('Building saved successfully!');
          navigate(`/building/view/${response.data.building_id}`);
        } else {
          toastError(
            'Failed to save building. Please try again.' +
              response.data.detail || '',
          );
        }
      } catch (err) {
        toastError(
          'Failed to save building. ' + (err.response?.data?.detail || ''),
        );
      }
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const isRental = building.type === 'rental';

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Add Building"
      actions={
        <>
          <RTButton variant="secondary" onClick={handleCancel}>
            &#8592; Back
          </RTButton>
        </>
      }
    >
      <form
        className="rt-main-section"
        onSubmit={handleSave}
        autoComplete="off"
      >
        <div className="rt-form-row">
          {/* Building Details */}
          <div className="rt-form-col">
            <h3 className="rt-form-title">Building Details</h3>
            <div className="rt-form-group">
              <label>
                Building Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={building.name}
                onChange={handleBuildingChange}
                className="rt-input"
              />
              {errors.name && (
                <div className="rt-form-error">{errors.name}</div>
              )}
            </div>
            <div className="rt-form-group">
              <label>
                Address <span style={{ color: 'red' }}>*</span>
              </label>
              <textarea
                name="address"
                value={building.address}
                onChange={handleBuildingChange}
                className="rt-input"
                rows={2}
              />
              {errors.address && (
                <div className="rt-form-error">{errors.address}</div>
              )}
            </div>
            <div className="rt-form-group">
              <label>Building Type</label>
              <div style={{ display: 'flex', gap: 18, marginTop: 6 }}>
                <label>
                  <input
                    type="radio"
                    name="type"
                    value="own"
                    checked={building.type === 'own'}
                    onChange={handleBuildingChange}
                  />{' '}
                  Own
                </label>
                <label>
                  <input
                    type="radio"
                    name="type"
                    value="rental"
                    checked={building.type === 'rental'}
                    onChange={handleBuildingChange}
                  />{' '}
                  Rental
                </label>
              </div>
            </div>
            <div className="rt-form-group">
              <label>
                Occupancy Gender <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                name="occupancyGender"
                value={building.occupancyGender}
                onChange={handleBuildingChange}
                className={`rt-input rt-dropdown${errors.occupancyGender ? ' rt-input-error' : ''}`}
              >
                <option value="">Select</option>
                {OCCUPANCY_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.occupancyGender && (
                <div className="rt-form-error">{errors.occupancyGender}</div>
              )}
            </div>
            <div className="rt-form-group">
              <label>
                Start Date <span style={{ color: 'red' }}>*</span>
              </label>
              <DatePicker
                selected={building.startDate}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                className="custom-datepicker rt-input"
                maxDate={new Date()}
                placeholderText="DD/MM/YYYY"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                autoComplete="off"
              />
              {errors.startDate && (
                <div className="rt-form-error">{errors.startDate}</div>
              )}
            </div>
          </div>
          {/* Rental Details */}
          <div className="rt-form-col">
            <h3 className="rt-form-title">Rental Details</h3>
            <div className="rt-form-group">
              <label>
                Owner Name {isRental && <span style={{ color: 'red' }}>*</span>}
              </label>
              <input
                type="text"
                name="ownerName"
                value={rental.ownerName}
                onChange={handleRentalChange}
                className="rt-input"
                disabled={!isRental}
              />
              {errors.ownerName && (
                <div className="rt-form-error">{errors.ownerName}</div>
              )}
            </div>
            <div className="rt-form-group">
              <label>Phone Number</label>
              <input
                type="text"
                name="ownerPhone"
                value={rental.ownerPhone}
                onChange={handleRentalChange}
                className="rt-input"
                disabled={!isRental}
              />
              {errors.ownerPhone && (
                <div className="rt-form-error">{errors.ownerPhone}</div>
              )}
            </div>
            <div className="rt-form-group">
              <label
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>Owner Address</span>
                {isRental && (
                  <label
                    style={{
                      fontSize: '0.9rem',
                      fontWeight: 'normal',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <input
                      type="checkbox"
                      onChange={handleSameAddressChange}
                      disabled={!isRental}
                    />
                    Same As Building Address
                  </label>
                )}
              </label>
              <textarea
                name="ownerAddress"
                value={rental.ownerAddress}
                onChange={handleRentalChange}
                className="rt-input"
                rows={2}
                disabled={!isRental}
              />
            </div>
            <div className="rt-form-group">
              <label>Advance</label>
              <div className="rt-input-rupee-wrapper">
                <span className="rt-rupee-symbol">₹ </span>
                <input
                  type="number"
                  name="advance"
                  value={rental.advance}
                  onChange={handleRentalChange}
                  className="rt-input rt-input-rupee"
                  disabled={!isRental}
                />
              </div>
              {errors.advance && (
                <div className="rt-form-error">{errors.advance}</div>
              )}
            </div>
            <div className="rt-form-group">
              <label>
                Monthly Rent (Including Maintenance){' '}
                {isRental && <span style={{ color: 'red' }}>*</span>}
              </label>
              <div className="rt-input-rupee-wrapper">
                <span className="rt-rupee-symbol">₹ </span>
                <input
                  type="number"
                  name="rent"
                  value={rental.rent}
                  onChange={handleRentalChange}
                  className="rt-input rt-input-rupee"
                  disabled={!isRental}
                  required={isRental}
                />
              </div>
              {errors.rent && (
                <div className="rt-form-error">{errors.rent}</div>
              )}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 16,
            marginTop: 32,
          }}
        >
          <RTButton variant="secondary" type="button" onClick={handleCancel}>
            Cancel
          </RTButton>
          <RTButton variant="primary" type="submit">
            Save
          </RTButton>
        </div>
      </form>
    </RTPageLayout>
  );
};

export default AddBuilding;
